import { useCallback, useState } from 'react';
import { APP_NAME } from '../config/appConfig';
import { useQuoteStats, useQuoteStatus, useQuoteUniverse } from '../data/stores/quoteStore';
import { Button } from '../ui/Button/Button';
import { MarketLogo } from '../ui/Logo/MarketLogo';
import { AddWindowModal } from '../workspace/AddWindowModal';
import { useWorkspace } from '../workspace/useWorkspace';
import { WorkspaceGrid } from '../workspace/WorkspaceGrid';
import { formatCompact } from '../shared/format';
import { IndexPanel } from '../windows/indexes/IndexPanel';
import { KeyboardShortcutsOverlay } from '../shortcuts/KeyboardShortcutsOverlay';
import { ShortcutCommandBar } from '../shortcuts/ShortcutCommandBar';
import type { ShortcutAction } from '../shortcuts/shortcuts';

export function App() {
  const workspace = useWorkspace();

  const [launcherOpen, setLauncherOpen] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  const status = useQuoteStatus();
  const stats = useQuoteStats();
  const universe = useQuoteUniverse();

  const windowCount = workspace.state.windows.length;

  const openCommandBar = useCallback(() => {
    setLauncherOpen(false);
    setShortcutHelpOpen(false);
    setCommandOpen(true);
  }, []);

  const runShortcut = useCallback((action: ShortcutAction) => {
    if (action.type === 'news') {
      workspace.addWindow('news');
      return;
    }

    if (action.type === 'quote') {
      workspace.addWindow('quote', { symbol: action.symbol });
      return;
    }

    if (action.type === 'chart') {
      workspace.openGraphForSymbol(action.symbol);
      return;
    }

    if (action.type === 'add') {
      setLauncherOpen(true);
      return;
    }

    if (action.type === 'reset') {
      workspace.resetWorkspace();
      return;
    }

    setShortcutHelpOpen(true);
  }, [workspace]);

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="app-sidebar__brand">
          <MarketLogo className="brand-logo" />
          <span className="brand-copy">
            <strong>{APP_NAME}</strong>
            <small>Using Mock Data</small>
          </span>
        </div>

        <nav className="app-sidebar__nav" aria-label="Terminal actions">
          <Button variant="primary" onClick={() => setLauncherOpen(true)}>
            Add Window
          </Button>

          <Button
            className="app-sidebar__reset"
            variant="secondary"
            onClick={workspace.resetWorkspace}
          >
            Reset
          </Button>

          <Button
            className="keyboard-shortcuts-button"
            variant="secondary"
            onClick={() => setShortcutHelpOpen(true)}
          >
            Keyboard shortcuts
          </Button>
        </nav>

        <IndexPanel />

        <div className="app-sidebar__stats">
          <dl>
            <div className={status.connected ? 'app-sidebar__connection app-sidebar__connection--active' : 'app-sidebar__connection'}>
              <dt>Connection</dt>
              <dd>
                <span className="connection-dot" aria-hidden="true" />
                <span>{status.connected ? 'ON' : 'OFF'}</span>
              </dd>
            </div>

            <div>
              <dt>Windows</dt>
              <dd>{windowCount}</dd>
            </div>

            <div>
              <dt>Quotes</dt>
              <dd>{formatCompact(stats.quoteCount)}</dd>
            </div>

            <div>
              <dt>Updates</dt>
              <dd>{formatCompact(stats.totalUpdates)}</dd>
            </div>

            <div>
              <dt>Dropped</dt>
              <dd>{formatCompact(stats.dropped)}</dd>
            </div>
          </dl>
        </div>
      </aside>

      <main className="app-main">
        <WorkspaceGrid
          windows={workspace.state.windows}
          layouts={workspace.state.layouts}
          onLayoutsChange={workspace.setLayouts}
          onRemoveWindow={workspace.removeWindow}
          onUpdateWindowConfig={workspace.updateWindowConfig}
          onFocusWindow={workspace.focusWindow}
          onOpenGraph={workspace.openGraphForSymbol}
          onOpenNewsDetail={workspace.openNewsDetail}
        />
      </main>

      <AddWindowModal
        open={launcherOpen}
        onClose={() => setLauncherOpen(false)}
        onAdd={workspace.addWindow}
      />

      <KeyboardShortcutsOverlay
        open={shortcutHelpOpen}
        onClose={() => setShortcutHelpOpen(false)}
      />

      <ShortcutCommandBar
        open={commandOpen}
        availableSymbols={universe}
        onOpen={openCommandBar}
        onClose={() => setCommandOpen(false)}
        onExecute={runShortcut}
      />
    </div>
  );
}